import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { experienceService } from '../../../../services/experienceService';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const AdminExperience = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      setLoading(true);
      const data = await experienceService?.getAllExperience();
      setExperiences(data);
    } catch (err) {
      console.error('Error fetching experiences:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) return;

    try {
      await experienceService?.deleteExperience(id);
      setExperiences(experiences?.filter(exp => exp?.id !== id));
    } catch (err) {
      console.error('Error deleting experience:', err);
      alert('Failed to delete experience');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Manage Experience - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">Manage Experience</h1>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/">
                  <Button variant="outline" size="sm" iconName="Home" />
                </Link>
                <Button variant="ghost" size="sm" iconName="LogOut" onClick={handleSignOut} />
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Experience</h2>
              <p className="text-muted-foreground">Manage all your work experience</p>
            </div>
            <Link to="/admin/dashboard/experience/new">
              <Button variant="default" iconName="Plus" iconPosition="left">
                New Experience
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading experience...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">{error}</p>
            </div>
          ) : experiences?.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Icon name="Award" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No experience yet</p>
              <Link to="/admin/dashboard/experience/new">
                <Button variant="default" iconName="Plus" iconPosition="left">
                  Create Your First Experience
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {experiences?.map((exp) => (
                <div
                  key={exp?.id}
                  className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground mb-1">
                            {exp?.role}
                          </h3>
                          <p className="text-base text-accent font-medium mb-2">
                            {exp?.company}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                            <span>{exp?.period}</span>
                            <span>•</span>
                            <span>{exp?.duration}</span>
                            <span>•</span>
                            <span>{exp?.location}</span>
                            <span>•</span>
                            <span className="px-2 py-1 bg-accent/10 text-accent rounded-md text-xs">
                              {exp?.type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mb-4">
                        <p className="text-sm text-muted-foreground mb-2">Achievements:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {exp?.achievements?.slice(0, 3)?.map((achievement, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground">
                              {achievement}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/admin/dashboard/experience/edit/${exp?.id}`}>
                          <Button variant="outline" size="sm" iconName="Edit" iconPosition="left">
                            Edit
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          iconPosition="left"
                          onClick={() => handleDelete(exp?.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminExperience;